import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getAnomalies } from '../api/client';

const TYPE_META: Record<string, { label: string; cls: string }> = {
  REPORT_FAILED: { label: 'Report failed', cls: 'bg-red-100 text-red-700' },
  SETUP_ERRORED: { label: 'Setup errored', cls: 'bg-amber-100 text-amber-800' },
  COMMENTARY_FAILED: { label: 'AI commentary failed', cls: 'bg-amber-100 text-amber-800' },
};

function timeAgo(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function AnomaliesPage() {
  const q = useQuery({ queryKey: ['anomalies'], queryFn: () => getAnomalies('30d'), staleTime: 5 * 60 * 1000 });
  const events = q.data?.events ?? [];
  const topChats = q.data?.topChats ?? [];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Anomalies</h1>
        <p className="mt-1 text-sm text-gray-500">
          Bot-side errors that block value or signal churn risk (report failures, setup errors) — last 30 days. Fix these before they cost a group.
        </p>
      </div>

      {q.isLoading ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : q.data?.total === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-emerald-50 px-4 py-6 text-sm text-emerald-700">
          No errors in the last 30 days. ✓
        </div>
      ) : (
        <>
          {topChats.length > 0 && (
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Top affected groups</h2>
              <div className="flex flex-wrap gap-2">
                {topChats.map((c) => (
                  <Link key={c.chatId} to={`/chats/${encodeURIComponent(c.chatId)}`}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs hover:bg-gray-50">
                    <span className="font-mono text-sky-700">{c.chatId}</span>
                    <span className="ml-2 rounded-full bg-red-100 px-1.5 py-0.5 tabular-nums text-red-700">{c.count}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Error feed · {q.data?.total ?? 0} events</h2>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Type</th>
                    <th className="px-4 py-2 font-medium">Chat</th>
                    <th className="px-4 py-2 font-medium">Detail</th>
                    <th className="px-4 py-2 font-medium">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {events.map((e, i) => {
                    const meta = TYPE_META[e.type] ?? { label: e.type, cls: 'bg-gray-100 text-gray-600' };
                    const detail = e.payload ? Object.entries(e.payload).map(([k, v]) => `${k}=${String(v)}`).join(' · ') : '—';
                    return (
                      <tr key={`${e.chatId}-${e.ts}-${i}`} className="hover:bg-gray-50">
                        <td className="px-4 py-2"><span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}>{meta.label}</span></td>
                        <td className="px-4 py-2">
                          <Link className="font-mono text-xs text-sky-700 hover:underline" to={`/chats/${encodeURIComponent(e.chatId)}`}>{e.chatId}</Link>
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-600">{detail}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-400">{timeAgo(e.ts)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
